import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { z } from 'zod'

import Fieldset from '@/components-latest/layout/Fieldset'
import Button from '@/components-latest/primitives/Button'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import NumericInput from '@/components-latest/primitives/Controls/NumericInput'
import {
  ExclamationPoint,
  Information
} from '@/components-latest/primitives/Icons'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import Accordion from '@/components-latest/shared/Accordion'
import { Form } from '@/components-latest/shared/Form'
import Suggestions, {
  SuggestionsOption
} from '@/components-latest/shared/Suggestions'
import { useUpdatePreparationTimeMutation } from '@/ducks/account/api/account.api'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'
import { pushGtmFormSubmitEvent } from '@/utils/gtm.utils'

import { mapInitPreparationToForm, validateMinMax } from './helpers'
import Styles from './PreparationTimeForm.module.scss'

type IInfoProps = {
  icon: React.ReactNode
  text: string
  className?: string
}
const Info = ({ icon, text, className }: IInfoProps) => {
  return (
    <Text variant="body" className={clsx(Styles.info, className)} asChild>
      <p>
        {icon}
        {text}
      </p>
    </Text>
  )
}

const preparationTimeFormSchema = z.object({
  min: z
    .number()
    .nullable()
    .refine(value => value !== null && value > 0, {
      message: 'Must be a positive number or null'
    }),
  max: z
    .number()
    .nullable()
    .refine(value => value !== null && value > 0, {
      message: 'Must be a positive number or null'
    })
})

export type IPreparationTimeForm = z.infer<typeof preparationTimeFormSchema>

const initialFormData = {
  min: null,
  max: null
}

type Props = {
  isLoading?: boolean
  initPreparationData?: IPreparationTimeForm
}
const PreparationTimeForm = ({ isLoading, initPreparationData }: Props) => {
  const { t } = useTranslation()

  const form = useForm<IPreparationTimeForm>({
    resolver: zodResolver(preparationTimeFormSchema),
    mode: 'onSubmit',
    defaultValues: initialFormData,
    reValidateMode: 'onSubmit',
    shouldFocusError: false
  })

  const storeId = useSelector(selectStoreId)
  const dispatch = useDispatch()

  const {
    handleSubmit,
    clearErrors,
    reset,
    watch,
    setError,
    setValue,
    formState: { errors, isDirty }
  } = form

  const [updatePreparationTime, { isLoading: isSubmitting }] =
    useUpdatePreparationTimeMutation()

  useEffect(() => {
    if (!initPreparationData) return
    reset(mapInitPreparationToForm(initPreparationData))
  }, [initPreparationData, reset])

  const minValue = watch('min')
  const maxValue = watch('max')

  const onTimeValueChangeCreator =
    (fieldName: keyof IPreparationTimeForm) =>
    (value: string | number | null) => {
      setValue(fieldName, value === null ? null : Number(value), {
        shouldDirty: true
      })
    }

  const timeSuggestions = useMemo(() => {
    const timeStep = 5
    const minTime = 15

    return new Array(9)
      .fill(null)
      .map((_, index) => {
        return {
          value: index * timeStep,
          text: t('account.preparation-time.input-suggestion', {
            number: index * timeStep
          })
        }
      })
      .filter(({ value }) => value >= minTime)
  }, [t])

  const onSubmit = (values: IPreparationTimeForm) => {
    if (!storeId || !values.min || !values.max) return

    if (!validateMinMax(values)) {
      const message =
        'From should be less then To and difference should be at least 5 minutes'
      setError('min', { message })
      setError('max', { message })
      return
    }

    pushGtmFormSubmitEvent()

    updatePreparationTime({
      min: values.min * 60000,
      max: values.max * 60000,
      store_id: storeId
    })
      .unwrap()
      .then(({ message }) => {
        if (message === 'success') {
          dispatch(
            addSystemNotification({
              message: t('account.preparation-time.success'),
              intent: 'success',
              duration: 3000
            })
          )
        } else {
          dispatch(
            addSystemNotification({
              message: t('account.preparation-time.error'),
              intent: 'error',
              duration: 3000
            })
          )
        }
      })
      .catch(() => {
        dispatch(
          addSystemNotification({
            message: t('account.preparation-time.error'),
            intent: 'error',
            duration: 3000
          })
        )
      })
  }

  return (
    <ContentBlock className={Styles.preparationWrapper}>
      {isLoading ? (
        <div className={Styles.spinner}>
          <Spinner />
        </div>
      ) : (
        <Accordion
          label={t('account.preparation-time.title')}
          icon={<ExclamationPoint />}>
          <Form {...form}>
            <form className={Styles.form} onSubmit={handleSubmit(onSubmit)}>
              <div className={Styles.inputs}>
                <Fieldset
                  variant="nextBody"
                  weight="semiBold"
                  label={t('account.preparation-time.from')}>
                  <Suggestions
                    onSelect={onTimeValueChangeCreator('min')}
                    trigger={
                      <NumericInput
                        intent={errors?.min?.message ? 'negative' : 'default'}
                        onFocus={() => {
                          clearErrors()
                        }}
                        value={minValue}
                        data-testid="preparation-time-min-input"
                        onChange={onTimeValueChangeCreator('min')}
                        readOnly
                        step={5}
                        max={
                          maxValue !== null && maxValue !== undefined
                            ? maxValue - 1
                            : undefined
                        }
                      />
                    }>
                    {timeSuggestions.map(({ value, text }) => (
                      <SuggestionsOption
                        key={value}
                        value={value}
                        disabled={
                          maxValue !== null ? value >= maxValue : false
                        }>
                        {text}
                      </SuggestionsOption>
                    ))}
                  </Suggestions>
                </Fieldset>
                <div className={Styles.separator} />
                <Fieldset
                  variant="nextBody"
                  weight="semiBold"
                  label={t('account.preparation-time.to')}>
                  <Suggestions
                    onSelect={onTimeValueChangeCreator('max')}
                    trigger={
                      <NumericInput
                        value={maxValue}
                        intent={errors?.max?.message ? 'negative' : 'default'}
                        onFocus={() => {
                          clearErrors()
                        }}
                        data-testid="preparation-time-max-input"
                        onChange={onTimeValueChangeCreator('max')}
                        step={5}
                        readOnly
                        min={
                          minValue !== null && minValue !== undefined
                            ? minValue + 1
                            : undefined
                        }
                      />
                    }>
                    {timeSuggestions.map(({ value, text }) => (
                      <SuggestionsOption
                        key={value}
                        value={value}
                        disabled={
                          minValue !== null ? value <= minValue : false
                        }>
                        {text}
                      </SuggestionsOption>
                    ))}
                  </Suggestions>
                </Fieldset>
              </div>
              <Info
                className={Styles.infoItem}
                icon={<Information className={Styles.infoIcon} />}
                text={t('account.preparation-time.text-1')}
              />
              <Info
                className={Styles.infoItem}
                icon={<Information />}
                text={t('account.preparation-time.text-2')}
              />
              <Button
                variant="primary"
                disabled={!isDirty || !maxValue || !minValue || isSubmitting}
                loading={isSubmitting}
                className={Styles.submitButton}>
                {t('account.preparation-time.submit')}
              </Button>
            </form>
          </Form>
        </Accordion>
      )}
    </ContentBlock>
  )
}

export default PreparationTimeForm
